import { createContext, ReactNode, useContext, useEffect, useRef, useState } from "react";
import { flushSync } from "react-dom";

export type UserRole = "owner" | "gm" | "sales" | "tech" | "viewer";
export type PaidFeature = "speed_to_lead";
export type FeatureMap = Partial<Record<PaidFeature | string, boolean>>;
type RefreshOptions = {
  clearOnFailure?: boolean;
};

export interface User {
  id: number;
  username: string;
  full_name: string | null;
  email: string | null;
  role: UserRole;
  tenant_id: number;
  is_demo?: boolean;
  must_change_password: boolean;
  features?: FeatureMap;
}

interface AuthContextValue {
  user: User | null;
  accessToken: string | null;
  loading: boolean;
  login: (username: string, password: string, remember: boolean) => Promise<void>;
  logout: () => Promise<void>;
  refresh: (options?: RefreshOptions) => Promise<string | null | undefined>;
  getAuthVersion: () => number;
  isAuthVersionCurrent: (version: number) => boolean;
  hasRole: (...roles: UserRole[]) => boolean;
  hasFeature: (...features: PaidFeature[]) => boolean;
}

const AuthContext = createContext<AuthContextValue | null>(null);

function normalizeUser(user: User): User {
  return {
    ...user,
    features: user.features ?? {},
  };
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const authVersion = useRef(0);

  async function refresh(options: RefreshOptions = {}) {
    const { clearOnFailure = true } = options;
    const requestVersion = authVersion.current;
    const res = await fetch("/api/auth/refresh", {
      method: "POST",
      credentials: "include",
    });
    if (!res.ok) {
      if (authVersion.current === requestVersion && clearOnFailure) {
        setAccessToken(null);
        setUser(null);
      }
      return authVersion.current === requestVersion ? null : undefined;
    }
    const data = (await res.json()) as { access_token: string };
    if (authVersion.current !== requestVersion) return undefined;
    setAccessToken(data.access_token);
    const meRes = await fetch("/api/auth/me", {
      headers: { Authorization: `Bearer ${data.access_token}` },
      credentials: "include",
    });
    if (authVersion.current !== requestVersion) return undefined;
    if (meRes.ok) {
      const me = (await meRes.json()) as { user: User };
      setUser(normalizeUser(me.user));
    }
    return data.access_token;
  }

  useEffect(() => {
    refresh({ clearOnFailure: false }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    if (!accessToken) return undefined;
    const timer = window.setTimeout(() => {
      refresh().catch(() => undefined);
    }, 13 * 60 * 1000);
    return () => window.clearTimeout(timer);
  }, [accessToken]);

  async function login(username: string, password: string, remember: boolean) {
    authVersion.current += 1;
    const res = await fetch("/api/auth/login", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify({ username, password, remember }),
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ error: "Login failed" }));
      throw new Error(err.error || "Login failed");
    }
    const data = (await res.json()) as { access_token: string; user: User };
    flushSync(() => {
      setAccessToken(data.access_token);
      setUser(normalizeUser(data.user));
    });
  }

  async function logout() {
    authVersion.current += 1;
    if (accessToken) {
      await fetch("/api/auth/logout", {
        method: "POST",
        headers: { Authorization: `Bearer ${accessToken}` },
        credentials: "include",
      }).catch(() => undefined);
    }
    setAccessToken(null);
    setUser(null);
  }

  function hasRole(...roles: UserRole[]) {
    return user ? roles.includes(user.role) : false;
  }

  function hasFeature(...features: PaidFeature[]) {
    if (!user) return false;
    return features.every((feature) => user.features?.[feature] === true);
  }

  function getAuthVersion() {
    return authVersion.current;
  }

  function isAuthVersionCurrent(version: number) {
    return authVersion.current === version;
  }

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        loading,
        login,
        logout,
        refresh,
        getAuthVersion,
        isAuthVersionCurrent,
        hasRole,
        hasFeature,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
}
